import {
  batchAssignImpositionSchema,
  outsourceToolingSchema,
  receiveToolingSchema,
} from "@apv-erp/trpc";
import { z } from "zod";
import { trpcClient } from "./trpc";

export type OutsourceToolingInput = z.infer<typeof outsourceToolingSchema>;
export type ReceiveToolingInput = z.infer<typeof receiveToolingSchema>;
export type BatchAssignImpositionInput = z.infer<
	typeof batchAssignImpositionSchema
>;

export interface GetUnassignedOrderDetailsForPrepressInput {
	customerId?: string | null;
	search?: string | null;
	excludeIds?: string[];
}

export interface OutsourceToolingResult {
	success: boolean;
	outsourcePO?: { id: string; poId: string };
	tooling?: { id: string; status: string };
}

export interface UnassignedOrderDetail {
	id: string;
	orderId: string;
	orderCode?: string;
	productName?: string;
	quantity?: number;
	customerId?: string;
	customerName?: string;
	materialId?: string;
	[key: string]: unknown;
}

export interface ProductionOrderDetail {
	id: string;
	poId?: string;
	status?: string;
	qtyPlan?: number;
	sheetSize?: string;
	productName?: string;
	routingSteps?: Array<{
		id: string;
		name?: string;
		machineId?: string;
		[key: string]: unknown;
	}>;
	components?: Array<{
		id: string;
		routingSteps?: Array<{ id: string; name?: string; [key: string]: unknown }>;
		[key: string]: unknown;
	}>;
	product?: {
		id: string;
		name?: string;
		productName?: string;
		unit?: string;
		shortName?: string;
		specs?: Record<string, unknown> | string | null;
		[key: string]: unknown;
	} | null;
	customer?: {
		id: string;
		name?: string;
		shortName?: string;
		[key: string]: unknown;
	} | null;
	salesOrder?: {
		customer?: {
			id: string;
			name?: string;
			shortName?: string;
			[key: string]: unknown;
		} | null;
		[key: string]: unknown;
	} | null;
	[key: string]: unknown;
}

const _client: {
	productionOrders: {
		outsourceTooling: {
			mutate: (input: OutsourceToolingInput) => Promise<OutsourceToolingResult>;
		};
		receiveTooling: {
			mutate: (input: ReceiveToolingInput) => Promise<unknown>;
		};
		getById: {
			query: (input: { id: string }) => Promise<ProductionOrderDetail>;
		};
	};
	orders: {
		getUnassignedOrderDetailsForPrepress: {
			query: (
				input?: GetUnassignedOrderDetailsForPrepressInput,
			) => Promise<UnassignedOrderDetail[]>;
		};
		batchAssignImposition: {
			mutate: (input: BatchAssignImpositionInput) => Promise<unknown>;
		};
	};
} = trpcClient;

export async function outsourceToolingMutate(
	input: OutsourceToolingInput,
): Promise<OutsourceToolingResult> {
	return _client.productionOrders.outsourceTooling.mutate(input);
}

export async function receiveToolingMutate(input: ReceiveToolingInput) {
	return _client.productionOrders.receiveTooling.mutate(input);
}

export async function getUnassignedOrderDetailsForPrepressQuery(
	input?: GetUnassignedOrderDetailsForPrepressInput,
): Promise<UnassignedOrderDetail[]> {
	return _client.orders.getUnassignedOrderDetailsForPrepress.query(input);
}

export async function batchAssignImpositionMutate(
	input: BatchAssignImpositionInput,
) {
	return _client.orders.batchAssignImposition.mutate(input);
}

export async function getProductionOrderByIdQuery(input: {
	id: string;
}): Promise<ProductionOrderDetail> {
	return _client.productionOrders.getById.query(input);
}
